/**
 * ハイライトCRUD共通hook
 *
 * fetch / update / delete のコアロジックを共通化
 * PDF, Image, EPUB の各hookから利用される
 */

import { useCallback, useEffect, useState } from 'react'

import { deleteHighlight, getEpisodeHighlights, type Highlight, updateHighlight } from '@/api/services/highlight'

import { HighlightColor } from '../highlight/highlight-colors'

type UseHighlightCrudParams = {
  episodeId?: number
  /** update/delete完了後の追加処理（EPUBのannotation削除等） */
  onAfterUpdate?: (id: number, updated: Highlight) => void
  onAfterDelete?: (id: number, highlight: Highlight | undefined) => void
}

type UseHighlightCrudReturn = {
  highlights: Highlight[]
  setHighlights: React.Dispatch<React.SetStateAction<Highlight[]>>
  handleUpdateHighlight: (id: number, color: HighlightColor, note: string) => Promise<void>
  handleDeleteHighlight: (id: number) => Promise<void>
}

/**
 * ハイライトの取得・更新・削除を一元管理する共通hook
 */
export function useHighlightCrud({
  episodeId,
  onAfterUpdate,
  onAfterDelete,
}: UseHighlightCrudParams): UseHighlightCrudReturn {
  const [highlights, setHighlights] = useState<Highlight[]>([])

  // ハイライト一覧を取得
  useEffect(() => {
    if (!episodeId) return
    const fetchHighlights = async () => {
      try {
        const data = await getEpisodeHighlights(episodeId)
        setHighlights(data)
      } catch (error) {
        console.error('ハイライト取得に失敗しました', error)
      }
    }
    fetchHighlights()
  }, [episodeId])

  // ハイライト更新処理
  const handleUpdateHighlight = useCallback(
    async (id: number, color: HighlightColor, note: string) => {
      try {
        const updated = await updateHighlight(id, { color, note: note || undefined })
        onAfterUpdate?.(id, updated)
        setHighlights((prev) => prev.map((h) => (h.id === id ? updated : h)))
      } catch (error) {
        console.error('ハイライト更新に失敗しました:', error)
      }
    },
    [onAfterUpdate],
  )

  // ハイライト削除処理
  const handleDeleteHighlight = useCallback(
    async (id: number) => {
      try {
        const highlight = highlights.find((h) => h.id === id)
        await deleteHighlight(id)
        onAfterDelete?.(id, highlight)
        setHighlights((prev) => prev.filter((h) => h.id !== id))
      } catch (error) {
        console.error('ハイライト削除に失敗しました:', error)
      }
    },
    [highlights, onAfterDelete],
  )

  return {
    highlights,
    setHighlights,
    handleUpdateHighlight,
    handleDeleteHighlight,
  }
}
